import { Globe, Award, Landmark, Star } from "lucide-react";

const DIGPU_LOGO = "https://digpu.com/wp-content/uploads/2025/07/logo-regular.png";
const WS_LOGO_WHITE = "https://www.waterandshark.com/assets/logos/W%26S%20White.png";

const partners = [
  {
    role: "Presenting Media Partner",
    name: "Digpu News & Media",
    desc: "India's fast-growing independent digital media and PR agency. International presence. Trusted by global diplomats and business leaders.",
    logo: DIGPU_LOGO,
    logoBg: "white",
    icon: Globe,
    testid: "sponsor-digpu",
  },
  {
    role: "Knowledge & Advisory Partner",
    name: "Water & Shark",
    desc: "Global advisory firm headquartered in Singapore. Cross-border tax, legal, regulatory advisory across 15+ jurisdictions since 2012.",
    logo: WS_LOGO_WHITE,
    logoBg: "#002ccc",
    icon: Star,
    testid: "sponsor-ws",
  },
  {
    role: "Co-Organising Partner",
    name: "EICBI",
    fullName: "Europe India Centre for Business and Industry",
    desc: "European Parliament-accredited. European Commission-registered. Strengthening EU-India strategic engagement since 2011.",
    logo: "https://customer-assets.emergentagent.com/job_elite-policy-summit/artifacts/2tnpeg8f_eicbi.png",
    logoBg: "white",
    icon: Landmark,
    testid: "sponsor-eicbi",
  },
  {
    role: "Diplomatic Host",
    name: "High Commission of Cyprus",
    desc: "Republic of Cyprus diplomatic mission to India, facilitating EU-India bilateral engagement at the highest level.",
    logo: "https://flagcdn.com/w40/cy.png",
    logoBg: "white",
    icon: Award,
    testid: "sponsor-cyprus",
  },
];

const placeholders = [
  { slot: "Platinum Sponsor", desc: "Your organisation here" },
  { slot: "Gold Sponsor", desc: "Your organisation here" },
  { slot: "Supporting Partner", desc: "Your organisation here" },
];

// Extract theme config to reduce function complexity
function getThemeConfig(theme) {
  const isDark = theme === "dark" || theme === "aurora";
  return {
    isDark,
    sectionBg: theme === "light" ? "bg-[#F5F0E8]" : "",
    cardBg: isDark
      ? "glass-card"
      : "bg-white border border-[#E2D9C6] shadow-sm hover:shadow-md transition-shadow",
    textMain: isDark ? "text-white" : "text-[#1C1A17]",
    textMuted: isDark ? "text-[#94A3B8]" : "text-[#6B6460]",
    goldColor: isDark ? "text-[#D4AF37]" : "text-[#C9A84C]",
    dividerClass: isDark ? "gold-divider" : "h-px bg-[#C9A84C]/40",
    overlineClass: isDark ? "overline-gold" : "text-[0.68rem] tracking-[0.2em] uppercase font-bold text-[#C9A84C]",
    placeholderBg: isDark
      ? "border border-dashed border-[#D4AF37]/25 bg-[#0A1128]/30"
      : "border border-dashed border-[#C9A84C]/30 bg-[#FAF8F3]",
  };
}

// Renders a flag image; falls back to a styled text label if the image fails to load
function FlagItem({ src, label, isDark }) {
  return (
    <div className="relative flex items-center justify-center">
      <img
        src={src}
        alt={label}
        className="h-5 w-auto border border-white/10"
        onError={(e) => {
          e.target.style.display = "none";
          e.target.nextElementSibling.style.display = "flex";
        }}
      />
      <span
        style={{ display: "none" }}
        className={`text-[0.65rem] font-['Outfit'] tracking-wider px-2 py-0.5 border ${isDark ? "border-[#D4AF37]/30 text-[#D4AF37]" : "border-[#C9A84C]/30 text-[#C9A84C]"}`}
      >
        {label}
      </span>
    </div>
  );
}

export default function SponsorsSection({ theme = "dark" }) {
  const { isDark, sectionBg, cardBg, textMain, textMuted, goldColor, dividerClass, overlineClass, placeholderBg } = getThemeConfig(theme);

  return (
    <section id="sponsors" data-testid="sponsors-section"
      className={`${sectionBg} py-24 lg:py-28 px-6`}
      style={isDark ? { background: "#080705" } : {}}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <p className={`${overlineClass} mb-3`}>Partners &amp; Promoters</p>
          <h2 className={`font-['Cormorant_Garamond'] text-3xl lg:text-4xl font-medium ${textMain}`}>
            Organisations Behind This Dialogue
          </h2>
          <div className={`${dividerClass} w-48 mx-auto mt-5`} />
          <p className={`${textMuted} text-sm font-['Outfit'] mt-4 max-w-xl mx-auto`}>
            A coalition of globally respected media, advisory, and policy institutions united to bring this exclusive dialogue to Gujarat's business community.
          </p>
        </div>

        {/* Main partner cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {partners.map(({ role, name, fullName, desc, logo, logoBg, icon: Icon, testid }) => (
            <div key={testid} data-testid={testid} className={`${cardBg} p-6 flex flex-col gap-4`}>
              {/* Logo or icon */}
              <div className="h-14 flex items-center">
                {logo ? (
                  <div
                    className="h-10 px-2 flex items-center"
                    style={{ background: logoBg || "transparent", maxWidth: "120px" }}
                  >
                    <img
                      src={logo}
                      alt={name}
                      data-testid={`${testid}-logo`}
                      className="max-h-8 max-w-full object-contain"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  </div>
                ) : (
                  <div className={`w-12 h-12 flex items-center justify-center border ${isDark ? "border-[#D4AF37]/25 bg-[#D4AF37]/10" : "border-[#C9A84C]/25 bg-[#C9A84C]/10"}`}>
                    <Icon size={20} className={goldColor} />
                  </div>
                )}
              </div>

              <div>
                <p className={`${overlineClass} text-[0.58rem] mb-1`}>{role}</p>
                <h3 className={`font-['Cormorant_Garamond'] text-lg font-semibold ${textMain} leading-tight`}>{name}</h3>
                {fullName && <p className={`${textMuted} text-[0.68rem] mt-0.5`}>{fullName}</p>}
              </div>
              <p className={`${textMuted} text-xs font-['Outfit'] leading-relaxed flex-1`}>{desc}</p>
            </div>
          ))}
        </div>

        {/* Sponsorship opportunities */}
        <div className="mt-8">
          <p className={`${overlineClass} text-center text-[0.6rem] mb-6`}>Sponsorship Opportunities</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {placeholders.map(({ slot, desc }) => (
              <div key={slot} data-testid={`sponsor-placeholder-${slot.replace(/\s+/g, '-').toLowerCase()}`}
                className={`${placeholderBg} p-6 text-center flex flex-col items-center gap-3`}>
                <p className={`${overlineClass} text-[0.6rem]`}>{slot}</p>
                <p className={`${textMuted} text-xs font-['Outfit']`}>{desc}</p>
                <button className={`mt-1 text-[0.7rem] border ${isDark ? "border-[#D4AF37]/40 text-[#D4AF37]" : "border-[#C9A84C]/40 text-[#C9A84C]"} px-4 py-1.5 hover:bg-[#D4AF37]/10 transition-colors font-['Outfit'] tracking-wider uppercase`}>
                  Enquire
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Nations strip */}
        <div className="mt-10 flex items-center justify-center gap-6">
          <div className={`h-px flex-1 max-w-32 ${isDark ? "bg-[#D4AF37]/20" : "bg-[#C9A84C]/20"}`} />
          <div className="flex items-center gap-4">
            {[
              { src: "https://flagcdn.com/w32/eu.png", label: "EU" },
              { src: "https://flagcdn.com/w32/cy.png", label: "Cyprus" },
              { src: "https://flagcdn.com/w32/in.png", label: "India" },
            ].map(({ src, label }) => (
              <FlagItem key={label} src={src} label={label} isDark={isDark} />
            ))}
          </div>
          <div className={`h-px flex-1 max-w-32 ${isDark ? "bg-[#D4AF37]/20" : "bg-[#C9A84C]/20"}`} />
        </div>
      </div>
    </section>
  );
}
